import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import Stream from 'flarum/common/utils/Stream';

/**
 * Modal dialog that allows the current user to change their username.
 *
 * Opens from: Account Settings pane → "Change Username" button.
 */
export default class ChangeUsernameModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);

    /** @type {Stream<string>} Controlled input value */
    this.username = Stream('');

    /** @type {Stream<string>} Inline error message */
    this.errorMessage = Stream('');

    /** @type {boolean} Request in-flight */
    this.submitting = false;
  }

  className() {
    return 'ChangeUsernameModal Modal--small';
  }

  title() {
    return (
      <span>
        <i className="fas fa-user-edit" style="margin-right:8px;opacity:.85" />
        {app.translator.trans('happydigital-username-history.forum.modal_title')}
      </span>
    );
  }

  content() {
    const cooldownDays = parseInt(app.forum.attribute('happydigital-username-history.cooldownDays') || 0, 10);

    return (
      <div className="Modal-body">
        {/* Inline error banner */}
        {this.errorMessage() && (
          <div className="Alert Alert--error HappyDigital-alert">
            <i className="fas fa-exclamation-circle" />
            {' '}
            {this.errorMessage()}
          </div>
        )}

        {/* Cooldown hint */}
        {cooldownDays > 0 && (
          <p className="helpText HappyDigital-cooldownHint">
            <i className="fas fa-clock" />
            {' '}
            {app.translator.trans(
              'happydigital-username-history.forum.cooldown_hint',
              { '{days}': cooldownDays }
            )}
          </p>
        )}

        <div className="Form-group">
          <label htmlFor="hd-new-username">
            {app.translator.trans('happydigital-username-history.forum.new_username_label')}
          </label>
          <input
            id="hd-new-username"
            className="FormControl"
            type="text"
            bidi={this.username}
            placeholder={app.translator.trans(
              'happydigital-username-history.forum.new_username_placeholder'
            )}
            autocomplete="off"
            disabled={this.submitting}
          />
          <p className="helpText">
            {app.translator.trans('happydigital-username-history.forum.username_requirements')}
          </p>
        </div>

        <div className="Form-group">
          <Button
            className="Button Button--primary Button--block"
            loading={this.submitting}
            onclick={this.onsubmit.bind(this)}
            disabled={!this.username() || this.submitting}
          >
            {app.translator.trans('happydigital-username-history.forum.save_button')}
          </Button>
        </div>
      </div>
    );
  }

  onsubmit() {
    this.submitting = true;
    this.errorMessage('');
    m.redraw();

    app
      .request({
        method: 'POST',
        url: app.forum.attribute('baseUrl') + '/api/happydigital/change-username',
        body: {
          username: this.username(),
        },
        errorHandler: (error) => {
          // Suppress default Flarum alert — we handle it ourselves
          throw error;
        },
      })
      .then(() => {
        // Refresh the page so the new username is reflected everywhere
        window.location.reload();
      })
      .catch((error) => {
        this.submitting = false;

        // Try to extract the first validation error detail
        let message =
          error?.response?.errors?.[0]?.detail ||
          error?.message ||
          app.translator.trans('core.lib.error.generic_message');

        this.errorMessage(message);
        m.redraw();
      });
  }
}
